import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eyebrow } from './brand';
import { Estrella } from '../opiniones/EstrellasInput';
import { getSsrData } from '../../libs/ssrData';
import {
  OPINIONES_EN_HOME,
  formatearPromedio,
  resumenOpiniones,
  type OpinionPublica,
  type OpinionesSnapshot,
} from '../../helpers/opinionesHelper';

/**
 * Home: opiniones reales aprobadas por el admin de sitio (snapshot del build,
 * public/opiniones-snapshot.json). Sin opiniones aprobadas, la sección no se
 * muestra. El mismo contenido (promedio, cantidad y las 6 más recientes) va en
 * el HTML prerenderizado y en el schema (aggregateRating), ver prerender-seo.mjs.
 */

const Estrellas: React.FC<{ valor: number; size?: number }> = ({ valor, size = 18 }) => (
  <span style={{ display: 'inline-flex', gap: 2, color: 'var(--lp-niebla)' }} aria-label={`${valor} de 5 estrellas`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Estrella key={n} llena={n <= Math.round(valor)} size={size} />
    ))}
  </span>
);

const OpinionesSection: React.FC = () => {
  // En el render estático del build vienen precargadas (ver libs/ssrData).
  const [opiniones, setOpiniones] = useState<OpinionPublica[]>(() => getSsrData<OpinionPublica[]>('opiniones') ?? []);

  useEffect(() => {
    // Sin fetch (navegadores muy viejos, jsdom): la sección simplemente no se muestra.
    if (typeof fetch !== 'function') return;
    let activo = true;
    fetch('/opiniones-snapshot.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? (r.json() as Promise<OpinionesSnapshot>) : null))
      .then((snap) => {
        if (activo && snap && Array.isArray(snap.opiniones)) setOpiniones(snap.opiniones);
      })
      .catch(() => {
        /* sin snapshot: la sección no se muestra */
      });
    return () => {
      activo = false;
    };
  }, []);

  if (opiniones.length === 0) return null;
  const resumen = resumenOpiniones(opiniones);

  return (
    <section id="opiniones" className="lp-pad" aria-labelledby="opiniones-titulo">
      <div className="lp-wrap">
        <div className="lp-sec-h">
          <Eyebrow>Opiniones</Eyebrow>
          <h2 id="opiniones-titulo" className="lp-h2">
            Lo usan en campos como el tuyo.
          </h2>
          <p className="lp-lead">
            <Estrellas valor={resumen.promedio} size={22} />{' '}
            <strong>{formatearPromedio(resumen.promedio)} de 5</strong> según {resumen.cantidad}{' '}
            {resumen.cantidad === 1 ? 'opinión' : 'opiniones'} de productores, publicadas con su permiso.
          </p>
        </div>
        <div
          style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', marginTop: 36 }}
        >
          {opiniones.slice(0, OPINIONES_EN_HOME).map((o) => (
            <figure key={o.id} className="lp-card" style={{ margin: 0 }}>
              <Estrellas valor={o.calificacion} />
              <blockquote style={{ margin: '12px 0 16px', whiteSpace: 'pre-line' }}>{o.texto}</blockquote>
              <figcaption className="lp-note" style={{ fontSize: 14 }}>
                <strong style={{ color: 'var(--lp-verde-campo)' }}>{o.nombre_publico}</strong>
                {o.establecimiento ? ` · ${o.establecimiento}` : ''} · {o.provincia}
              </figcaption>
            </figure>
          ))}
        </div>
        <p style={{ marginTop: 28 }}>
          <Link className="lp-link" to="/mi-opinion">
            ¿Ya usás MiCampoDigital? Dejá tu opinión
          </Link>
        </p>
      </div>
    </section>
  );
};

export default OpinionesSection;
